/**
 * Maps raw Ward strings from med error incident CSV to canonical department IDs.
 * OPD_Primary and OPD_PC are canonical for med_error_incident only (not submission).
 * Merges with ethicDepartmentMap for overlapping Thai names.
 */
import { ETHIC_DEPARTMENT_MAP } from './ethicDepartmentMap';
import { UI_HIDDEN_DEPARTMENT_IDS } from './departmentUi';

/** Departments permanently omitted from nurse_notes submission UI and NN monthly scoreboard (all months). */
export const NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS = new Set<string>([
  ...UI_HIDDEN_DEPARTMENT_IDS,
  'OPD_Primary',
  'OPD_PC',
  'OPD_CHEMO',
]);

/**
 * Departments that do not submit นิเทศระบบยา (`med_error`).
 * Includes app-wide hidden wards (NSO, CSSD) plus G10, BLO_BNK, OPD_PC, OPD_Primary, OPD_CHEMO.
 */
export const MED_ERROR_SUPERVISION_EXCLUDED_DEPARTMENT_IDS = new Set<string>([
  ...UI_HIDDEN_DEPARTMENT_IDS,
  'G10',
  'BLO_BNK',
  'OPD_PC',
  'OPD_Primary',
  'OPD_CHEMO',
]);

export function isMedErrorSupervisionDepartmentExcluded(
  departmentId: string | null | undefined,
): boolean {
  if (!departmentId || typeof departmentId !== 'string') return false;
  const trimmed = departmentId.trim();
  if (!trimmed) return false;
  return MED_ERROR_SUPERVISION_EXCLUDED_DEPARTMENT_IDS.has(trimmed);
}

/** Med-error-specific mappings: raw Ward → canonical departmentId */
const MED_ERROR_WARD_MAP: Record<string, { departmentId: string; ipdOpd?: 'IPD' | 'OPD' }> = {
  '13DR': { departmentId: 'DR' },
  '13CNR': { departmentId: 'NICU' },
  '14NICU': { departmentId: 'NICU' },
  '14AMICU': { departmentId: 'MICU' },
  '11CSICU': { departmentId: 'SICU' },
  สว: { departmentId: 'SW' },
  burn: { departmentId: 'BURN' },
  ไตเทียม: { departmentId: 'HD' },
  ห้องส่องกล้อง: { departmentId: 'G10' },
  OPD1: { departmentId: 'OPD_MED' },
  OPD2: { departmentId: 'OPD_SxOrthoEENT' },
  OPD3: { departmentId: 'OPD_OBGYN' },
  'OPD4 PHC': { departmentId: 'OPD_Primary' },
  OPDจิตเวช: { departmentId: 'OPD_PC' },
  ER: { departmentId: 'ERD' },
  'ศูนย์บางพระ (OPD)': { departmentId: 'ECC', ipdOpd: 'OPD' },
  'ศูนย์บางพระ (IPD)': { departmentId: 'ECC', ipdOpd: 'IPD' },
};

/**
 * Resolves raw Ward to departmentId and optional ipdOpd override.
 * Falls back to ethicDepartmentMap for Thai names.
 */
export function resolveMedErrorDepartment(
  wardRaw: string | null | undefined,
): { departmentId: string; ipdOpd?: 'IPD' | 'OPD' } | null {
  if (!wardRaw || typeof wardRaw !== 'string') return null;
  const trimmed = wardRaw.trim();
  if (!trimmed) return null;

  // Case-insensitive for "burn"
  const key = trimmed.toLowerCase() === 'burn' ? 'burn' : trimmed;
  const medError = MED_ERROR_WARD_MAP[key] ?? MED_ERROR_WARD_MAP[trimmed];
  if (medError) return medError;

  // Fallback to ethic map (Thai names, direct department IDs)
  const ethicIds = ETHIC_DEPARTMENT_MAP[trimmed];
  if (ethicIds) {
    const id = Array.isArray(ethicIds) ? ethicIds[0] : ethicIds;
    return { departmentId: id };
  }

  // Direct match: ward is already canonical (e.g. 18B, 22A)
  const canonical = [
    '18A', '18B', '18C', '18D', '19A', '19B', '19C', '19D', '20B', '20C',
    '21A', '21D', '22A', '22B', '22C', '22D', '23A', '24A', '24B', '25A', '25B', '26B',
    'MICU', 'CCU', 'SICU', 'BURN', 'NICU', 'DR', 'HD', 'OR', 'CSSD', 'ERD',
    'OPD_MED', 'OPD_OBGYN', 'OPD_SxOrthoEENT', 'OPD_GENP', 'OPD_CHEMO',
    'OPD_Primary', 'OPD_PC', 'BLO_BNK', 'ECC', 'SW', 'G10', 'STROKE',
  ];
  if (canonical.includes(trimmed)) return { departmentId: trimmed };

  return null;
}
